import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Skeleton } from './ui/skeleton';
import { 
    Trophy, 
    Star, 
    Flame, 
    Zap, 
    Crown,
    Target,
    Medal,
    Gem,
    Diamond,
    Eye,
    Globe,
    Scale,
    Sparkles,
    Rocket,
    Coins,
    Brain
} from 'lucide-react';

// Icon mapping
const ICON_MAP = {
    trophy: Trophy,
    star: Star,
    flame: Flame,
    fire: Flame,
    zap: Zap,
    bolt: Zap,
    crown: Crown,
    target: Target,
    medal: Medal,
    gem: Gem,
    diamond: Diamond,
    eye: Eye,
    globe: Globe,
    scale: Scale,
    sparkles: Sparkles,
    rocket: Rocket,
    coins: Coins,
    brain: Brain
};

const getIcon = (iconName) => {
    return ICON_MAP[iconName] || Star;
};

export function BadgeDisplay({ badge, size = 'md', showDescription = true }) {
    const IconComponent = getIcon(badge.icon);
    const sizeClasses = {
        sm: 'w-8 h-8',
        md: 'w-12 h-12',
        lg: 'w-16 h-16'
    };
    const iconSizes = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-8 h-8'
    };
    
    return (
        <div className="flex items-center gap-3">
            <div 
                className={`${sizeClasses[size]} rounded-xl flex items-center justify-center`}
                style={{ backgroundColor: `${badge.color}20` }}
            >
                <IconComponent 
                    className={iconSizes[size]} 
                    style={{ color: badge.color }}
                />
            </div>
            {showDescription && (
                <div>
                    <div className="text-white font-semibold">{badge.name}</div>
                    <div className="text-white/50 text-sm">{badge.description}</div>
                </div>
            )}
        </div>
    );
}

export function LevelBadge({ level, xp, nextLevelXp }) {
    const progress = nextLevelXp 
        ? ((xp - level.xp_required) / (nextLevelXp - level.xp_required)) * 100
        : 100;
    
    return (
        <div className="flex items-center gap-4">
            <div 
                className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl"
                style={{ 
                    backgroundColor: `${level.color}20`,
                    color: level.color,
                    border: `2px solid ${level.color}`
                }}
            >
                {level.level}
            </div>
            <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-white font-semibold">{level.name}</span>
                    <span className="text-white/50 text-sm">{xp.toLocaleString()} XP</span>
                </div>
                {nextLevelXp && (
                    <div className="space-y-1">
                        <Progress value={progress} className="h-2 bg-white/10" />
                        <div className="text-xs text-white/40 text-right">
                            {nextLevelXp - xp} XP to next level
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export function BadgeProgressCard({ progress }) {
    const IconComponent = getIcon(progress.badge.icon);
    const percentage = progress.percentage;
    
    return (
        <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <IconComponent 
                        className="w-4 h-4" 
                        style={{ color: progress.badge.color }}
                    />
                    <span className="text-white text-sm font-medium">{progress.badge.name}</span>
                </div>
                <span className="text-white/50 text-xs">
                    {progress.current}/{progress.target}
                </span>
            </div>
            <Progress value={percentage} className="h-1.5 bg-white/10" />
        </div>
    );
}

export function GamificationPanel({ profile, loading = false }) {
    if (loading) {
        return (
            <Card className="bg-white/[0.02] border-white/5">
                <CardHeader>
                    <Skeleton className="h-6 w-40 bg-white/10" />
                </CardHeader>
                <CardContent className="space-y-4">
                    <Skeleton className="h-16 w-full bg-white/10" />
                    <Skeleton className="h-20 w-full bg-white/10" />
                </CardContent>
            </Card>
        );
    }
    
    if (!profile) return null;
    
    const { level, xp, next_level_xp, earned_badges, badge_progress } = profile;
    
    return (
        <Card className="bg-white/[0.02] border-white/5" data-testid="gamification-panel">
            <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FFD700]" />
                    Achievements
                </CardTitle>
                <CardDescription className="text-white/50">
                    Your level, XP, and badges
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Level & XP */}
                <LevelBadge level={level} xp={xp} nextLevelXp={next_level_xp} />
                
                {/* Earned Badges */}
                {earned_badges && earned_badges.length > 0 && (
                    <div>
                        <div className="text-sm text-white/60 mb-3">
                            Earned Badges ({earned_badges.length})
                        </div>
                        <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                            {earned_badges.map((badge, idx) => (
                                <div 
                                    key={idx}
                                    className="group relative"
                                    title={badge.description}
                                >
                                    <BadgeDisplay badge={badge} size="sm" showDescription={false} />
                                    <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#00FF66] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="text-[8px] text-[#0A0A0C] font-bold">!</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Badge Progress */}
                {badge_progress && badge_progress.length > 0 && (
                    <div>
                        <div className="text-sm text-white/60 mb-3">
                            Next Badges
                        </div>
                        <div className="space-y-2">
                            {badge_progress.slice(0, 3).map((progress, idx) => (
                                <BadgeProgressCard key={idx} progress={progress} />
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Empty State */}
                {(!earned_badges || earned_badges.length === 0) && (
                    <div className="text-center py-4">
                        <Sparkles className="w-8 h-8 text-white/20 mx-auto mb-2" />
                        <p className="text-white/40 text-sm">No badges yet</p>
                        <p className="text-white/30 text-xs">Complete activities to earn badges!</p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export function BadgesGrid({ badges, earnedIds = [] }) {
    const categories = ['activity', 'wins', 'streaks', 'volume', 'skill', 'special'];
    
    return (
        <div className="space-y-6">
            {categories.map(category => {
                const categoryBadges = badges.filter(b => b.category === category);
                if (categoryBadges.length === 0) return null;
                
                return (
                    <div key={category}>
                        <h3 className="text-white font-semibold capitalize mb-3">{category}</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                            {categoryBadges.map(badge => {
                                const isEarned = earnedIds.includes(badge.id);
                                return (
                                    <div 
                                        key={badge.id}
                                        className={`p-4 rounded-xl border ${
                                            isEarned 
                                                ? 'bg-white/[0.05] border-white/10' 
                                                : 'bg-white/[0.02] border-white/5 opacity-50'
                                        }`}
                                    >
                                        <BadgeDisplay 
                                            badge={badge} 
                                            size="md" 
                                            showDescription={true}
                                        />
                                        {isEarned && (
                                            <Badge className="mt-2 bg-[#00FF66]/20 text-[#00FF66] text-xs">
                                                Earned
                                            </Badge>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
